'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

/**
 * TooltipProvider
 *
 * Wrapper component that provides tooltip context to all child tooltips.
 * Should be placed high in the component tree.
 *
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <App />
 * </TooltipProvider>
 * ```
 */
const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Tooltip Component
 *
 * A lightweight tooltip component built on Radix UI's Tooltip primitive.
 * Supports multiple positions (top, bottom, left, right) and keyboard accessibility.
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <TooltipTrigger asChild>
 *     <button>Hover me</button>
 *   </TooltipTrigger>
 *   <TooltipContent>
 *     This is a helpful tooltip
 *   </TooltipContent>
 * </Tooltip>
 * ```
 */
const Tooltip = TooltipPrimitive.Root;

/**
 * TooltipTrigger
 *
 * The element that triggers the tooltip on hover or focus.
 * Can be rendered as a child element using asChild prop.
 */
const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * TooltipContent
 *
 * The tooltip content container. Appears near the trigger element.
 * Supports different positions via the side prop.
 *
 * Positions:
 * - 'top' (default): Appears above the trigger
 * - 'bottom': Appears below the trigger
 * - 'left': Appears to the left of the trigger
 * - 'right': Appears to the right of the trigger
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    /**
     * Position of the tooltip relative to the trigger
     * @default 'top'
     */
    side?: 'top' | 'bottom' | 'left' | 'right';
  }
>(
  (
    {
      className,
      side = 'top',
      sideOffset = 4,
      ...props
    },
    ref
  ) => (
    <TooltipPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={sideOffset}
      className={cn(
        'z-[var(--z-index-tooltip)] inline-flex items-center rounded-md',
        'bg-neutral-900 px-3 py-1.5 text-sm font-medium',
        'text-text-inverse',
        'shadow-md',
        'animate-in fade-in-0 zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
